function readDirectoryEntries(reader) {
    return new Promise((resolve, reject) => {
        const entries = [];

        function readBatch() {
            reader.readEntries(
                (batch) => {
                    if (!batch.length) {
                        resolve(entries);
                        return;
                    }

                    entries.push(...batch);
                    readBatch();
                },
                (error) => reject(error)
            );
        }

        readBatch();
    });
}

function readEntryFile(entry) {
    return new Promise((resolve, reject) => {
        entry.file(resolve, reject);
    });
}

function getEntryPath(entry) {
    if (!entry) {
        return '';
    }

    const rawPath = typeof entry.fullPath === 'string' && entry.fullPath
        ? entry.fullPath
        : entry.name || '';

    return String(rawPath).replace(/\\/g, '/');
}

function splitEntryPath(entry) {
    const normalizedPath = getEntryPath(entry);
    return normalizedPath.split('/').filter(Boolean);
}

function filterOverlappingRootEntries(entries) {
    const normalized = entries
        .filter(Boolean)
        .map((entry) => ({entry, path: getEntryPath(entry)}))
        .filter(({path}) => Boolean(path))
        .sort((a, b) => a.path.length - b.path.length);

    const accepted = [];

    normalized.forEach((candidate) => {
        const isNestedUnderAcceptedRoot = accepted.some((acceptedCandidate) => {
            const acceptedPath = acceptedCandidate.path.endsWith('/') ? acceptedCandidate.path : `${acceptedCandidate.path}/`;
            return candidate.path === acceptedCandidate.path || candidate.path.startsWith(acceptedPath);
        });

        if (!isNestedUnderAcceptedRoot) {
            accepted.push(candidate);
        }
    });

    return accepted.map(({entry}) => entry);
}

async function walkFileSystemEntry(entry, parentFolders = []) {
    if (!entry) {
        return [];
    }

    if (entry.isFile) {
        const file = await readEntryFile(entry);
        const derivedPathParts = parentFolders.length
            ? parentFolders
            : splitEntryPath(entry).slice(0, -1);

        return [{file, folderPath: derivedPathParts}];
    }

    if (!entry.isDirectory) {
        return [];
    }

    const reader = entry.createReader();
    const childEntries = await readDirectoryEntries(reader);
    const nested = await Promise.all(
        childEntries.map((childEntry) => walkFileSystemEntry(childEntry, [...parentFolders, entry.name]))
    );

    return nested.flat();
}

function toFallbackEntries(fileList) {
    return Array.from(fileList || []).map((file) => {
        const relativePath = file?.webkitRelativePath || '';
        const parts = relativePath ? relativePath.split('/').filter(Boolean) : [];

        return {
            file,
            folderPath: parts.length > 1 ? parts.slice(0, -1) : [],
        };
    });
}

export async function extractDroppedUploadEntries(dataTransfer) {
    if (!dataTransfer) {
        return [];
    }

    const items = Array.from(dataTransfer.items || []);
    const supportsEntries = items.some((item) => typeof item?.webkitGetAsEntry === 'function');

    if (!supportsEntries) {
        return toFallbackEntries(dataTransfer.files);
    }

    const rootEntries = filterOverlappingRootEntries(items
        .map((item) => item.webkitGetAsEntry?.())
        .filter(Boolean));

    if (!rootEntries.length) {
        return toFallbackEntries(dataTransfer.files);
    }

    const resolved = await Promise.all(rootEntries.map((entry) => walkFileSystemEntry(entry, [])));
    const entries = resolved.flat().filter((entry) => entry?.file);
    const uniqueEntries = [];
    const seenKeys = new Set();

    entries.forEach((entry) => {
        const path = [...(entry.folderPath || []), entry.file.name].join('/');
        const key = `${path}::${entry.file.size}::${entry.file.lastModified}`;
        if (seenKeys.has(key)) {
            return;
        }

        seenKeys.add(key);
        uniqueEntries.push(entry);
    });

    return uniqueEntries.length ? uniqueEntries : toFallbackEntries(dataTransfer.files);
}
